import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProducts } from '../../context/ProductContext';
import { Subcategory, getFileUrl, getProductTranslationKey } from '../../services/api';
import './ServicesPage.css';

// Category mapping based on service names (supports multiple languages)
const categoryMapping: Record<string, string[]> = {
  Security: [
    'CCTV Solution', 'Solution CCTV', '视频监控解决方案',
    'Intrusion Solution', 'Solution d\'intrusion', '入侵报警解决方案',
    'Perimeter Security', 'Sécurité périmétrique', '周界防范',
    'Security Metal Detector', 'Détecteur de métaux de sécurité', '安检门',
    'Security Baggage Solution', 'Solution de bagages de sécurité', '行李安检解决方案',
    'Security Solution', 'Solution de sécurité', '安防解决方案',
    'Security Turnstile Solution', 'Solution de tourniquet de sécurité', '闸机解决方案',
    'Automatic Number Plate Recognition', 'Reconnaissance automatique des plaques d\'immatriculation', '车牌识别',
    'Parking Management', 'Gestion de parking', '停车场管理',
    'Weighbridge Integration', 'Intégration de pont-bascule', '地磅集成'
  ],
  Access: [
    'Data Centre Access Control', 'Contrôle d\'accès au centre de données', '数据中心门禁',
    'Elevator Access Control', 'Contrôle d\'accès aux ascenseurs', '电梯门禁',
    'Time Attendance', 'Gestion du temps et des présences', '考勤系统',
    'Visitor Management', 'Gestion des visiteurs', '访客管理',
    'Contract Worker Management', 'Gestion des travailleurs contractuels', '承包商管理',
    'Employee Self Service', 'Libre-service employé', '员工自助服务',
    'Field Visit Management', 'Gestion des visites sur le terrain', '外勤管理',
    'Job Processing and Costing', 'Traitement des tâches et coûts', '作业处理与成本核算'
  ],
  IT: [
    'IT Infrastructure Solution', 'IT Infrastrucure Solution', 'Solution d\'infrastructure informatique', 'IT基础设施解决方案',
    'IT Peripheral', 'Périphérique informatique', 'IT外设',
    'PC and Laptop', 'PC et ordinateur portable', '电脑与笔记本',
    'Server and Storage', 'Serveur et stockage', '服务器与存储',
    'Data and WiFi Solution', 'Solution de données et WiFi', '数据与WiFi解决方案',
    'WiFi Solution', 'Solution WiFi', 'WiFi解决方案',
    'Telecom Solution', 'Solution de télécommunications', '通信解决方案',
    'UTM', 'UTM统一威胁管理',
    'UPS Solution', 'Solution UPS', 'UPS不间断电源'
  ],
  Display: [
    'LED Display Solution', 'Solution d\'affichage LED', 'LED显示屏解决方案',
    'Videowall Solution', 'Solution de mur vidéo', '视频墙解决方案',
    'Time Display Solution', 'Solution d\'affichage de l\'heure', '对时显示系统',
    'FIDS', '航班信息显示系统',
    'BGM Solution', '背景音乐系统',
    'PAVA Solution', '公共广播与语音疏散',
    'Meeting Room Solution', 'Solution de salle de réunion', '会议室解决方案'
  ],
  Facility: [
    'Cafeteria Management', 'Gestion de cafétéria', '食堂管理',
    'Baggage Conveyor Solution', 'Solution de convoyeur à bagages', '行李传送解决方案',
    'Nurse Calling Solution', 'Solution d\'appel infirmier', '护士呼叫系统'
  ]
};

const ServicesPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { productServiceCategory, softwareCategory, loading, error, generateSlug } = useProducts();
  const [selectedFilter, setSelectedFilter] = useState<string>(searchParams.get('filter') || 'All');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const categoryParam = searchParams.get('category') || 'products';
  const activeCategory = (categoryParam === 'software' ? 'software' : 'products') as 'products' | 'software';

  const subcategories = useMemo(() => {
    if (activeCategory === 'software') {
      return softwareCategory?.subcategories || [];
    }
    return productServiceCategory?.subcategories || [];
  }, [activeCategory, softwareCategory, productServiceCategory]);


  // Get category for a subcategory
  const getSubcategoryCategory = (subcategoryName: string): string => {
    for (const [category, services] of Object.entries(categoryMapping)) {
      if (services.some(service =>
        subcategoryName.toLowerCase() === service.toLowerCase()
      )) {
        return category;
      }
    }
    return 'Other';
  };

  // Filter subcategories based on selected filter and search query
  const filteredSubcategories = useMemo(() => {
    let filtered = subcategories;

    // Apply category filter
    if (selectedFilter !== 'All') {
      filtered = filtered.filter(subcategory => {
        const category = getSubcategoryCategory(subcategory.categoriesName);
        return category === selectedFilter;
      });
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(subcategory =>
        subcategory.categoriesName.toLowerCase().includes(query) ||
        (subcategory.shortDescription && subcategory.shortDescription.toLowerCase().includes(query)) ||
        (subcategory.briefDescription && subcategory.briefDescription.toLowerCase().includes(query)) ||
        (subcategory.products.length > 0 && subcategory.products.some(product =>
          product.name.toLowerCase().includes(query) ||
          (product.desc && product.desc.toLowerCase().includes(query))
        ))
      );
    }

    return filtered;
  }, [subcategories, selectedFilter, searchQuery]);

  const getSubcategoryDescription = (subcategory: Subcategory): string => {
    const translationKey = getProductTranslationKey(subcategory.categoriesName);
    const translatedDescription = t(`${translationKey}.description`);

    if (translatedDescription && translatedDescription !== `${translationKey}.description`) {
      return translatedDescription;
    }

    if (subcategory.shortDescription) {
      return subcategory.shortDescription;
    }
    if (subcategory.briefDescription) {
      return subcategory.briefDescription;
    }
    if (subcategory.products.length > 0 && subcategory.products[0].desc) {
      return subcategory.products[0].desc.substring(0, 150) + '...';
    }
    return t('servicesPage.defaultDescription', { name: subcategory.categoriesName });
  };

  // Reset filter when category changes or URL filter changes
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    setSelectedFilter(filterParam || 'All');
    setSearchQuery('');
  }, [activeCategory, searchParams]);

  return (
    <main className="services-grid-page">
      <div className="services-grid-container">
        {/* Header Section */}
        <div className="services-grid-header">
          <div className="services-category-badge">
            [{activeCategory === 'software' ? t('servicesPage.hero.softwareSolutions') : t('servicesPage.hero.productsServices')}]
          </div>
          <h1 className="services-grid-title">{t('servicesPage.hero.title')}</h1>
          <p className="services-grid-subtitle">
            {t('servicesPage.hero.subtitle')}
          </p>
        </div>

        {/* Filter and Search Section */}
        {!loading && !error && subcategories.length > 0 && (
          <div className="services-filter-section">
            {/* Filter Buttons */}
            <div className="services-filter-buttons">
              <button
                className={`filter-button ${selectedFilter === 'All' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('All')}
              >
                {t('servicesPage.filters.all')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Security' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Security')}
              >
                {t('servicesPage.filters.security')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Access' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Access')}
              >
                {t('servicesPage.filters.access')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'IT' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('IT')}
              >
                {t('servicesPage.filters.it')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Display' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Display')}
              >
                {t('servicesPage.filters.display')}
              </button>
              <button
                className={`filter-button ${selectedFilter === 'Facility' ? 'active' : ''}`}
                onClick={() => setSelectedFilter('Facility')}
              >
                {t('servicesPage.filters.facility')}
              </button>
            </div>

            {/* Search Input */}
            <div className="services-search-wrapper">
              <input
                type="text"
                className="services-search-input"
                placeholder={t('servicesPage.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
              </svg>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="services-grid-loading">
            <div className="loading-spinner"></div>
            <p>{t('servicesPage.loading')}</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="services-grid-error">
            <p>{t('servicesPage.error')}</p>
          </div>
        )}

        {/* Services Grid */}
        {!loading && !error && filteredSubcategories.length > 0 && (
          <div className="services-grid">
            {filteredSubcategories.map((subcategory) => (
              <ServiceCard
                key={subcategory.id}
                subcategory={subcategory}
                activeCategory={activeCategory}
                generateSlug={generateSlug}
                getDescription={getSubcategoryDescription}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredSubcategories.length === 0 && (
          <div className="services-grid-empty">
            <p>
              {searchQuery.trim() || selectedFilter !== 'All'
                ? t('servicesPage.empty.noneFound')
                : (activeCategory === 'software' ? t('servicesPage.empty.noSoftware') : t('servicesPage.empty.noProducts'))}
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

interface ServiceCardProps {
  subcategory: Subcategory;
  activeCategory: 'products' | 'software';
  generateSlug: (name: string) => string;
  getDescription: (subcategory: Subcategory) => string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({
  subcategory,
  activeCategory,
  generateSlug,
  getDescription
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => {
      video.play().catch(console.error);
    };

    const handleError = () => {
      console.error('Video failed to load');
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    video.muted = true;
    video.load();

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, []);

  const videoUrl = subcategory.video ? getFileUrl(subcategory.video) : null;
  const description = getDescription(subcategory);
  const route = `/${activeCategory === 'software' ? 'software' : 'product'}/${generateSlug(subcategory.categoriesName)}`;

  return (
    <div className="service-card">
      <Link to={route} className="service-card-link">
        <div className="service-card-video-wrapper">
          {videoUrl ? (
            <video
              ref={videoRef}
              className="service-card-video"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
            >
              <source src={videoUrl} type="video/mp4" />
            </video>
          ) : (
            <div className="service-card-placeholder">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
              </svg>
            </div>
          )}
          <div className="service-card-overlay"></div>

          {/* Service Name - Always Visible */}
          <div className="service-card-title-overlay">
            <h3 className="service-card-title">
              {(() => {
                const translationKey = getProductTranslationKey(subcategory.categoriesName);
                const translatedName = t(`${translationKey}.title`);
                return translatedName && translatedName !== `${translationKey}.title`
                  ? translatedName
                  : subcategory.categoriesName;
              })()}
            </h3>
          </div>

          {/* Description - Shows on Hover */}
          <div className="service-card-description-overlay">
            <p className="service-card-description">{description}</p>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default ServicesPage;
